import type { FunctionComponent, RefObject } from 'react';
import { useLayoutEffect } from 'react';

const chipObserver: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
  entries.forEach((entry) => {
    (entry.target as HTMLElement).style.setProperty('font-size', `${entry.contentRect.width}px`);
  });
});

const valueObserver: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
  entries.forEach((entry: ResizeObserverEntry) => {
    // 2.5 is a magic number that makes the text fit the chip to 90% of its width
    const number = (entry.contentRect.height * 2.5) / entry.contentRect.width;
    (entry.target as HTMLElement).style.setProperty('--scale', number.toString());
  });
});

interface ChipObserverProps {
  chipRef: RefObject<HTMLDivElement>;
  valueRef: RefObject<HTMLDivElement>;
}

export const ChipCalculationHook: FunctionComponent<ChipObserverProps> = ({ chipRef, valueRef }) => {
  useLayoutEffect(() => {
    if (!chipRef.current || !valueRef.current) return;

    const chip = chipRef.current;
    const value = valueRef.current;

    chipObserver.observe(chip);
    valueObserver.observe(value);

    return (): void => {
      chipObserver.unobserve(chip);
      valueObserver.unobserve(value);
    };
  }, [chipRef, valueRef]);

  return null;
};

export const getFontSize = (valueText: string): number => {
  const digits = valueText.length;
  let fontSize: number = 0;
  const isDot = /[.,]/.test(valueText);

  switch (digits) {
    case 1:
    case 2:
      fontSize = 400;
      break;
    case 3:
      fontSize = isDot ? 400 : 360;
      break;
    case 4:
      fontSize = 320;
      break;
    default:
      if (digits > 4) {
        fontSize = 280;
      }
      break;
  }
  return fontSize;
};
